import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  BookOpen,
  Sparkles,
  Map,
  MessageSquare,
  Feather,
  Trophy,
  Globe2,
  Quote,
  ShieldCheck,
  Users,
  Lightbulb,
  Languages,
  Compass,
} from 'lucide-react';
import { useT } from '../context/LanguageContext';

const LEARN_POINTS = [
  'Traduire des mots, des phrases et des textes avec précision.',
  'Comprendre les nuances entre traduction littérale et traduction naturelle.',
  'Découvrir les expressions idiomatiques et les faux amis.',
  'Développer un vocabulaire plus riche et plus précis.',
  "Comparer différentes façons d'exprimer une même idée.",
  'Traduire des dialogues, des histoires et des situations authentiques.',
  'Comprendre les références culturelles qui influencent la langue.',
  'Améliorer votre compréhension écrite et votre expression écrite.',
  'Explorer la traduction littéraire et créative.',
  "Construire progressivement les compétences d'un véritable médiateur entre les langues.",
];

const SECTIONS = [
  {
    id: 'premiers-pas',
    eyebrow: 'A1 – A2',
    title: 'Premiers Pas',
    icon: Compass,
    desc: "Vos premiers voyages entre les langues : mots du quotidien, phrases simples, premières découvertes.",
  },
  {
    id: 'expressions-en-voyage',
    eyebrow: 'Idiomes',
    title: 'Expressions en Voyage',
    icon: Sparkles,
    desc: 'Expressions idiomatiques, proverbes, faux amis — les mots qui voyagent autrement.',
  },
  {
    id: 'histoires-voyageuses',
    eyebrow: 'Récits',
    title: 'Histoires Voyageuses',
    icon: BookOpen,
    desc: 'Traduisez de courtes histoires bilingues et observez comment le sens passe d\'une rive à l\'autre.',
  },
  {
    id: 'defis-de-traduction',
    eyebrow: 'Pratique',
    title: 'Défis de Traduction',
    icon: Trophy,
    desc: 'Des défis progressifs pour aiguiser votre précision linguistique et votre créativité.',
  },
  {
    id: 'les-intraduisibles',
    eyebrow: 'Mots uniques',
    title: 'Les Intraduisibles',
    icon: Globe2,
    desc: "Ces mots magnifiques qui n'existent que dans une seule langue — et l'art de les approcher.",
  },
  {
    id: 'traduire-la-litterature',
    eyebrow: 'Littérature',
    title: 'Traduire la Littérature',
    icon: Feather,
    desc: 'Extraits, poèmes, dialogues littéraires — la traduction comme acte de création.',
  },
  {
    id: 'portfolio',
    eyebrow: 'Votre cahier',
    title: 'Portfolio des Mots Voyageurs',
    icon: Lightbulb,
    desc: 'Collectionnez vos meilleures traductions, vos découvertes et votre progression au fil du temps.',
  },
  {
    id: 'communaute',
    eyebrow: 'Échange',
    title: 'Communauté des Mots Voyageurs',
    icon: Users,
    desc: 'Partagez vos traductions, commentez celles des autres, débattez du choix des mots.',
  },
];

const JOURNEY_FEELS = [
  { icon: BookOpen, t: 'Livres-bulles', d: 'Des livres ouverts qui deviennent des paroles vivantes.' },
  { icon: Map, t: 'Cartes des mots', d: "Des mots qui traversent les frontières et changent d'habit." },
  { icon: Languages, t: 'Ponts linguistiques', d: 'Des ponts entre les idées, les cultures et les sensibilités.' },
];

export default function LesMotsEnVoyage() {
  const t = useT();
  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="les-mots-en-voyage-page">
      {/* HERO */}
      <section className="pt-16 pb-14">
        <div className="max-w-[920px] mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="text-[12px] text-[#6B4E37] mb-8 flex items-center gap-1.5 flex-wrap" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Selora</Link>
            <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
            <span className="text-[#1A2F4A] font-semibold">Les Mots en Voyage</span>
          </nav>

          <div className="text-center">
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#D4AF37]/50" />
              <Languages className="w-5 h-5 text-[#D4AF37]" />
              <div className="h-px w-8 bg-[#D4AF37]/50" />
            </div>
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-3">
              {t({ fr: "D'une langue à l'autre", en: 'From one language to another' })}
            </p>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A2F4A] mb-5 leading-[1.05]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="page-title">
              Les Mots en Voyage
            </h1>

            <p
              className="text-base sm:text-lg text-[#6B4E37] italic max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="hero-tagline">
              {t({
                fr: <>Voyagez d'une langue à l'autre et découvrez le pouvoir des mots.<br className="hidden sm:block" />Quand les mots changent de langue, ils racontent une nouvelle histoire.</>,
                en: <>Travel from one language to another and discover the power of words.<br className="hidden sm:block" />When words change language, they tell a new story.</>,
              })}
            </p>
          </div>

          {/* Visual journey strip */}
          <div className="mt-10 grid sm:grid-cols-3 gap-3" data-testid="visual-journey">
            {JOURNEY_FEELS.map((j, i) => {
              const Icon = j.icon;
              return (
                <div key={i} className="bg-white border border-[#E8E4DC] rounded-2xl px-5 py-5 text-center" data-testid={`journey-feel-${i}`}>
                  <Icon className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-[13px] font-bold text-[#1A2F4A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{j.t}</p>
                  <p className="text-[11px] text-[#6B4E37] leading-relaxed">{j.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="pb-14" data-testid="introduction-section">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Introduction</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              Plus qu'une traduction — un voyage
            </h2>
          </div>

          <div className="space-y-5 text-[#1A2F4A]/90 text-[15px] leading-relaxed">
            <p>
              La traduction est bien plus qu'un simple passage d'une langue à une autre. C'est un voyage à travers les
              cultures, les idées et les façons de penser. Dans <em>Les Mots en Voyage</em>, vous apprendrez à
              comprendre le sens derrière les mots, à découvrir les nuances de chaque langue et à exprimer les mêmes
              idées de manière naturelle et authentique.
            </p>
            <p>
              À travers des exercices progressifs, des expressions idiomatiques, des défis de traduction, des textes
              littéraires et des situations de la vie réelle, vous développerez votre précision linguistique,
              enrichirez votre vocabulaire et renforcerez votre compréhension du français. Chaque activité vous aidera
              à explorer comment les mots voyagent d'une langue à l'autre tout en conservant leur intention, leur
              émotion et leur richesse culturelle.
            </p>
          </div>

          <figure
            className="mt-10 bg-white border-l-2 border-[#D4AF37] rounded-r-xl px-6 py-5 relative"
            data-testid="intro-pledge">
            <Quote className="w-5 h-5 text-[#D4AF37]/70 absolute -top-2.5 left-5 bg-[#FAF7F0] px-0.5" />
            <blockquote
              className="text-[#1A2F4A] text-base leading-relaxed italic"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              « Chaque mot a une histoire. Chaque langue offre une nouvelle perspective. »
            </blockquote>
            <figcaption className="text-[10px] text-[#6B4E37] uppercase tracking-[0.25em] mt-3 font-bold">
              — Notre philosophie
            </figcaption>
          </figure>
        </div>
      </section>

      {/* WHAT YOU WILL LEARN */}
      <section className="pb-14 bg-white border-y border-[#E8E4DC]" data-testid="learn-section">
        <div className="max-w-[1000px] mx-auto px-6 pt-14">
          <div className="text-center mb-10">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Programme</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="learn-title">
              Dans cette section, vous apprendrez à :
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-3" data-testid="learn-grid">
            {LEARN_POINTS.map((p, i) => (
              <div
                key={i}
                className="bg-[#FAF7F0] border border-[#E8E4DC] hover:border-[#D4AF37] rounded-xl px-5 py-4 flex items-start gap-3 transition-all"
                data-testid={`learn-point-${i}`}>
                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4AF37] text-xs font-bold">{i + 1}</span>
                </div>
                <p className="text-[13.5px] text-[#1A2F4A] leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FUTURE LEARNING AREAS */}
      <section className="py-16" data-testid="sections-section">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Exploration</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="sections-title">
              Huit chemins pour faire voyager les mots
            </h2>
            <p className="text-sm text-[#6B4E37] italic max-w-xl mx-auto">
              Du premier mot traduit jusqu'à la littérature — chaque section ouvre un nouveau monde linguistique.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sections-grid">
            {SECTIONS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.id}
                  className="group bg-white border border-[#E8E4DC] hover:border-[#D4AF37] rounded-2xl px-5 py-5 transition-all hover:shadow-md flex flex-col h-full relative overflow-hidden"
                  data-testid={`section-${s.id}`}>
                  <div className="absolute top-3 right-3 text-[10px] text-[#D4AF37]/40 font-bold tracking-widest">
                    0{i + 1}
                  </div>
                  <div className="w-11 h-11 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/30 flex items-center justify-center mb-3 group-hover:bg-[#D4AF37]/10 transition-colors">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <p className="text-[9px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1">{s.eyebrow}</p>
                  <h3
                    className="text-base font-bold text-[#1A2F4A] leading-tight mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}>
                    {s.title}
                  </h3>
                  <p className="text-[12px] text-[#6B4E37] leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-4 pt-3 border-t border-[#F3F4F6] flex items-center gap-2 text-[10px] text-[#999] italic">
                    <Sparkles className="w-3 h-3 text-[#D4AF37]" />
                    <span>Bientôt disponible</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CRÉATION ORIGINALE BADGE */}
      <section className="pb-14" data-testid="creation-badge-section">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="bg-gradient-to-br from-[#1A2F4A] to-[#243a58] rounded-2xl px-7 py-7 text-white overflow-hidden relative">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#D4AF37]/10 rounded-full" aria-hidden="true" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold">Label éditorial</p>
                  <h3 className="text-xl font-bold leading-tight flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <span className="text-[#D4AF37]" aria-hidden="true">✦</span>
                    Création Originale Selora
                  </h3>
                </div>
              </div>
              <p className="text-[14px] text-white/90 leading-relaxed">
                Tous les contenus de <em>Les Mots en Voyage</em> sont conçus par l'équipe pédagogique de Selora pour
                développer votre précision linguistique, votre sensibilité culturelle et votre confiance d'expression
                — du niveau A1 au C1.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="pb-20" data-testid="closing-section">
        <div className="max-w-[760px] mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-8 bg-[#D4AF37]/50" />
            <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
            <div className="h-px w-8 bg-[#D4AF37]/50" />
          </div>
          <p className="text-xl sm:text-2xl text-[#1A2F4A] leading-relaxed italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            Chaque mot a une histoire.
            <br />
            Chaque langue offre une nouvelle perspective.
          </p>
          <p className="mt-5 text-[14px] text-[#6B4E37] leading-relaxed max-w-xl mx-auto">
            Dans <em>Les Mots en Voyage</em>, vous découvrirez comment faire voyager les idées, les émotions et les
            cultures d'un monde linguistique à un autre.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/speaking-lab"
              className="inline-flex items-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white px-6 py-3 rounded-full text-sm font-semibold transition-all"
              data-testid="cta-speaking-lab">
              Pratiquer à l'oral
              <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/lire-le-francais"
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C9A961] text-[#1A2F4A] px-6 py-3 rounded-full text-sm font-bold transition-all"
              data-testid="cta-lire">
              Lire le français
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
